import { auth } from "../firebase";

const PRIMARY_API_BASE = "https://api.ateno.co";
const FALLBACK_API_BASE = "https://us-central1-oyola-ai.cloudfunctions.net/api";

function getSameOriginBase() {
  if (typeof window === "undefined") return null;
  const { origin, hostname } = window.location;
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0";

  if (isLocalhost) return null;
  return String(origin || "").replace(/\/$/, "");
}

function normalizeBaseUrl(base) {
  const trimmed = String(base).replace(/\/$/, "");

  // Public API domain always serves backend endpoints under /api.
  const publicDomainNormalized = trimmed.replace(
    /^https:\/\/api\.ateno\.co(?:\/.*)?$/,
    "https://api.ateno.co/api"
  );

  // Accept legacy/misconfigured Cloud Functions URLs and force the Express API base.
  return publicDomainNormalized.replace(
    /(https:\/\/[^/]+\.cloudfunctions\.net)\/api_website(?:\/.*)?$/,
    "$1/api"
  );
}

function getBaseCandidates() {
  const configured = [
    process.env.NEXT_PUBLIC_API_BASE_URL,
    process.env.REACT_APP_API_URL,
    process.env.REACT_APP_API_BASE_URL,
  ]
    .filter(Boolean)
    .map((base) => normalizeBaseUrl(base));

  // Keep public API domain first to avoid exposing direct Cloud Functions URL unless needed.
  const sameOriginBase = getSameOriginBase();
  const all = [
    sameOriginBase,
    ...configured.filter(
      (base) => base !== sameOriginBase && base !== PRIMARY_API_BASE && base !== FALLBACK_API_BASE
    ),
    PRIMARY_API_BASE,
    FALLBACK_API_BASE,
  ];

  return [...new Set(all.filter(Boolean))];
}

function normalizePath(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function buildUrl(path, baseUrl) {
  const normalizedPath = normalizePath(path);
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;
  return `${String(baseUrl).replace(/\/$/, "")}${normalizedPath}`;
}

async function getFirebaseToken() {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status || 0;
    this.code = options.code || null;
    this.data = options.data;
    this.retryAfter = options.retryAfter || null;
    this.rateLimit = options.rateLimit || null;
    this.isRateLimit = this.status === 429;
  }
}

function getRetryAfter(headers, body) {
  const retryAfterHeader = headers.get("retry-after");
  const retryAfterBody = body?.retryAfter || body?.retry_after || null;
  return retryAfterBody || retryAfterHeader || null;
}

function getRateLimit(headers, body) {
  const limit = headers.get("x-ratelimit-limit") || body?.limit || body?.rateLimit?.limit;
  const remaining =
    headers.get("x-ratelimit-remaining") || body?.remaining || body?.rateLimit?.remaining;
  const reset = headers.get("x-ratelimit-reset") || body?.reset || body?.rateLimit?.reset;

  if (limit == null && remaining == null && reset == null) return null;
  return { limit, remaining, reset };
}

async function parseResponse(res) {
  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json().catch(() => null);
  }

  const text = await res.text().catch(() => "");
  return text || null;
}

export async function apiFetch(path, options = {}) {
  const {
    method = "GET",
    body,
    token,
    headers: inputHeaders,
    signal,
  } = options;

  const authToken = token || (await getFirebaseToken());
  const headers = {
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(inputHeaders || {}),
  };

  if (authToken && !headers.Authorization) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const isAbsolutePath = /^https?:\/\//i.test(normalizePath(path));
  const baseCandidates = isAbsolutePath ? [""] : getBaseCandidates();

  let res;
  let lastNetworkError = null;

  for (const base of baseCandidates) {
    try {
      res = await fetch(isAbsolutePath ? path : buildUrl(path, base), {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
      break;
    } catch (networkError) {
      lastNetworkError = networkError;
    }
  }

  if (!res) {
    throw new ApiError(lastNetworkError?.message || "Network request failed.", {
      status: 0,
      code: "network_error",
      data: { path },
    });
  }

  const payload = await parseResponse(res);

  if (!res.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      payload?.code ||
      `Request failed (${res.status})`;

    throw new ApiError(message, {
      status: res.status,
      code: payload?.code || null,
      data: payload,
      retryAfter: getRetryAfter(res.headers, payload),
      rateLimit: getRateLimit(res.headers, payload),
    });
  }

  return payload;
}

const api = {
  get: (path, options = {}) => apiFetch(path, { ...options, method: "GET" }),
  post: (path, body, options = {}) => apiFetch(path, { ...options, method: "POST", body }),
  patch: (path, body, options = {}) => apiFetch(path, { ...options, method: "PATCH", body }),
  delete: (path, options = {}) => apiFetch(path, { ...options, method: "DELETE" }),
};

export default api;
